import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import SidePanel, { SidePanelSection, SidePanelActions, SidePanelDivider } from '@/components/SidePanel';
import { ShoppingCart, Plus, Search, Trash2, Edit2, Truck, Check, Clock, X, Send, Package, FileText, Download, ChevronDown, ChevronRight, Eye } from 'lucide-react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { formatDate } from '@/utils/dateFormat';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-stone-100 text-stone-600', icon: Clock },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-600', icon: Send },
  acknowledged: { label: 'Acknowledged', color: 'bg-purple-100 text-purple-600', icon: Check },
  delivered: { label: 'Delivered', color: 'bg-green-100 text-green-600', icon: Truck },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-600', icon: X },
};

const UNIT_OPTIONS = ['Nos', 'Sq.ft', 'R.ft', 'L.ft', 'Kg', 'Ltr', 'Set', 'Pair', 'Box', 'Bag', 'Lot', 'Sheet', 'Bundle', 'Mtr', 'Sqm', 'Rmt', 'Cum'];

export default function PurchaseOrdersNew() {
  const [orders, setOrders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  
  // Side panel states
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMinimized, setPanelMinimized] = useState(false);
  const [panelMode, setPanelMode] = useState('create'); // create, edit, view
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    project_id: '',
    vendor_id: '',
    items: [{ item_name: '', description: '', quantity: 1, unit: 'Nos', rate: 0, amount: 0 }],
    delivery_address: '',
    delivery_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    terms: '',
    status: 'draft',
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchData = useCallback(async () => {
    try {
      const [ordersRes, projectsRes, usersRes] = await Promise.all([
        axios.get(`${API}/purchase-orders`),
        axios.get(`${API}/projects`).catch(() => ({ data: [] })),
        axios.get(`${API}/users`).catch(() => ({ data: [] })),
      ]);
      setOrders(ordersRes.data);
      setProjects(projectsRes.data);
      setVendors(usersRes.data.filter(u => u.role === 'vendor'));
    } catch (error) {
      toast.error('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      project_id: '',
      vendor_id: '',
      items: [{ item_name: '', description: '', quantity: 1, unit: 'Nos', rate: 0, amount: 0 }],
      delivery_address: '',
      delivery_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      terms: '',
      status: 'draft',
    });
  };

  const openCreatePanel = () => {
    resetForm();
    setPanelMode('create');
    setSelectedOrder(null);
    setPanelOpen(true);
  };

  const openEditPanel = (order) => {
    setSelectedOrder(order);
    setFormData({
      project_id: order.project_id || '',
      vendor_id: order.vendor_id || '',
      items: order.items || [{ item_name: '', description: '', quantity: 1, unit: 'Nos', rate: 0, amount: 0 }],
      delivery_address: order.delivery_address || '',
      delivery_date: order.delivery_date ? order.delivery_date.split('T')[0] : '',
      terms: order.terms || '',
      status: order.status || 'draft',
    });
    setPanelMode('edit');
    setPanelOpen(true);
  };

  const openViewPanel = (order) => {
    setSelectedOrder(order);
    setPanelMode('view');
    setPanelOpen(true);
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { item_name: '', description: '', quantity: 1, unit: 'Nos', rate: 0, amount: 0 }],
    });
  };

  const handleRemoveItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData({ ...formData, items: newItems });
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    if (field === 'quantity' || field === 'rate') {
      newItems[index].amount = Number(newItems[index].quantity) * Number(newItems[index].rate);
    }
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.amount || 0), 0);
  };

  const handleCreate = async () => {
    if (!formData.project_id || !formData.vendor_id) {
      toast.error('Project and vendor are required');
      return;
    }
    if (!formData.items.length || formData.items.some(i => !i.item_name)) {
      toast.error('Please add at least one item with name');
      return;
    }

    try {
      await axios.post(`${API}/purchase-orders`, {
        ...formData,
        delivery_date: new Date(formData.delivery_date).toISOString(),
      });
      toast.success('Purchase order created successfully');
      setPanelOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create purchase order');
    }
  };

  const handleUpdate = async () => {
    if (!selectedOrder) return;

    try {
      await axios.put(`${API}/purchase-orders/${selectedOrder.id}`, {
        ...formData,
        delivery_date: new Date(formData.delivery_date).toISOString(),
      });
      toast.success('Purchase order updated successfully');
      setPanelOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update purchase order');
    }
  };

  const handleStatusChange = async (order, newStatus) => {
    try {
      await axios.put(`${API}/purchase-orders/${order.id}`, { status: newStatus });
      toast.success(`Order marked as ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async () => {
    if (!selectedOrder) return;
    try {
      await axios.delete(`${API}/purchase-orders/${selectedOrder.id}`);
      toast.success('Purchase order deleted successfully');
      setDeleteDialogOpen(false);
      setPanelOpen(false);
      setSelectedOrder(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete purchase order');
    }
  };

  const handleExportPDF = async (order) => {
    try {
      const response = await axios.get(`${API}/purchase-orders/${order.id}/pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PO-${order.po_number || order.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const handleExportExcel = async (order) => {
    try {
      const response = await axios.get(`${API}/purchase-orders/${order.id}/excel`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PO-${order.po_number || order.id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel downloaded successfully');
    } catch (error) {
      toast.error('Failed to export Excel');
    }
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || 'N/A';
  };

  const getVendorName = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    return vendor?.name || 'N/A';
  };

  const toggleOrderExpand = (orderId) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  // Filter orders
  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      (order.po_number || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      getProjectName(order.project_id).toLowerCase().includes(searchQuery.toLowerCase()) ||
      getVendorName(order.vendor_id).toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Group orders by status
  const groupedOrders = Object.keys(STATUS_CONFIG).reduce((acc, status) => {
    acc[status] = filteredOrders.filter(o => o.status === status);
    return acc;
  }, {});

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-stone-400">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="purchase-orders-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-manrope text-3xl md:text-4xl font-bold tracking-tight text-stone-900 mb-2">
              Purchase Orders
            </h1>
            <p className="text-stone-500">Manage vendor orders and deliveries</p>
          </div>
          {(user.role === 'owner' || user.role === 'staff') && (
            <Button onClick={openCreatePanel} className="apple-button" data-testid="create-po-btn">
              <Plus size={18} className="mr-2" />
              New Purchase Order
            </Button>
          )}
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
            <Input
              placeholder="Search by PO number, project, or vendor..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white"
              data-testid="search-po"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48 bg-white">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {Object.entries(STATUS_CONFIG).map(([status, config]) => {
            const StatusIcon = config.icon;
            const count = groupedOrders[status]?.length || 0;
            return (
              <div 
                key={status}
                className={`apple-card p-4 cursor-pointer hover:shadow-md transition-shadow ${statusFilter === status ? 'ring-2 ring-blue-400' : ''}`}
                onClick={() => setStatusFilter(statusFilter === status ? 'all' : status)}
              >
                <div className="flex items-center gap-3 mb-2">
                  <StatusIcon size={20} className={config.color.split(' ')[1]} />
                  <span className="text-2xl font-bold">{count}</span>
                </div>
                <p className="text-sm text-stone-500">{config.label}</p>
              </div>
            );
          })}
        </div>

        {/* Orders List */}
        <div className="space-y-3">
          {filteredOrders.length === 0 ? (
            <div className="apple-card p-12 text-center">
              <ShoppingCart className="mx-auto mb-4 text-stone-300" size={48} />
              <p className="text-stone-500">No purchase orders found</p>
              <Button onClick={openCreatePanel} variant="outline" className="mt-4">
                <Plus size={16} className="mr-2" />
                Create First Order
              </Button>
            </div>
          ) : (
            filteredOrders.map((order) => {
              const isExpanded = expandedOrders.has(order.id);
              const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.draft;
              const StatusIcon = statusConfig.icon;
              const orderTotal = (order.items || []).reduce((sum, item) => sum + (item.amount || 0), 0);

              return (
                <div key={order.id} className="apple-card overflow-hidden" data-testid={`po-card-${order.id}`}>
                  {/* Order Header */}
                  <div 
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-stone-50 transition-colors"
                    onClick={() => toggleOrderExpand(order.id)}
                  >
                    <div className="flex items-center gap-4">
                      <button className="p-1 hover:bg-stone-200 rounded">
                        {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                      </button>
                      <div>
                        <div className="flex items-center gap-3">
                          <span className="font-semibold text-stone-900">
                            {order.po_number || `PO-${order.id.slice(0, 8)}`}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
                            <StatusIcon size={12} className="inline mr-1" />
                            {statusConfig.label}
                          </span>
                        </div>
                        <div className="text-sm text-stone-500 mt-1">
                          {getProjectName(order.project_id)} • {getVendorName(order.vendor_id)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="font-semibold text-stone-900">₹{orderTotal.toLocaleString()}</div>
                        <div className="text-xs text-stone-500">{order.items?.length || 0} items</div>
                      </div>
                      
                      {/* Actions Dropdown */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm">
                            <Package size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openViewPanel(order); }}>
                            <Eye size={14} className="mr-2" /> View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditPanel(order); }}>
                            <Edit2 size={14} className="mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleExportPDF(order); }}>
                            <FileText size={14} className="mr-2" /> Export PDF
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleExportExcel(order); }}>
                            <Download size={14} className="mr-2" /> Export Excel
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={(e) => { e.stopPropagation(); setSelectedOrder(order); setDeleteDialogOpen(true); }}
                            className="text-red-600"
                          >
                            <Trash2 size={14} className="mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="border-t border-stone-200 p-4 bg-stone-50">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-stone-500 uppercase">Delivery Date</p>
                          <p className="font-medium">{order.delivery_date ? formatDate(new Date(order.delivery_date)) : 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-stone-500 uppercase">Delivery Address</p>
                          <p className="font-medium text-sm">{order.delivery_address || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-stone-500 uppercase">Status Actions</p>
                          <Select value={order.status} onValueChange={(value) => handleStatusChange(order, value)}>
                            <SelectTrigger className="h-8 mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                                <SelectItem key={key} value={key}>{config.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Items Table */}
                      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-stone-100">
                            <tr>
                              <th className="text-left px-4 py-2 font-medium text-stone-600">Item</th>
                              <th className="text-left px-4 py-2 font-medium text-stone-600">Description</th>
                              <th className="text-center px-4 py-2 font-medium text-stone-600">Qty</th>
                              <th className="text-center px-4 py-2 font-medium text-stone-600">Unit</th>
                              <th className="text-right px-4 py-2 font-medium text-stone-600">Rate</th>
                              <th className="text-right px-4 py-2 font-medium text-stone-600">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(order.items || []).map((item, idx) => (
                              <tr key={idx} className="border-t border-stone-100">
                                <td className="px-4 py-2">{item.item_name}</td>
                                <td className="px-4 py-2 text-stone-500">{item.description || '-'}</td>
                                <td className="px-4 py-2 text-center">{item.quantity}</td>
                                <td className="px-4 py-2 text-center">{item.unit}</td>
                                <td className="px-4 py-2 text-right">₹{item.rate?.toLocaleString()}</td>
                                <td className="px-4 py-2 text-right font-medium">₹{item.amount?.toLocaleString()}</td>
                              </tr>
                            ))}
                            <tr className="border-t-2 border-stone-300 bg-stone-50">
                              <td colSpan="5" className="px-4 py-2 text-right font-semibold">Total</td>
                              <td className="px-4 py-2 text-right font-bold text-lg">₹{orderTotal.toLocaleString()}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {order.terms && (
                        <div className="mt-4">
                          <p className="text-xs text-stone-500 uppercase mb-1">Terms & Conditions</p>
                          <p className="text-sm text-stone-600 bg-white p-3 rounded border border-stone-200">{order.terms}</p>
                        </div>
                      )}

                      {/* Quick Actions */}
                      <div className="flex gap-2 mt-4">
                        <Button variant="outline" size="sm" onClick={() => openEditPanel(order)}>
                          <Edit2 size={14} className="mr-2" /> Edit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleExportPDF(order)}>
                          <FileText size={14} className="mr-2" /> PDF
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleExportExcel(order)}>
                          <Download size={14} className="mr-2" /> Excel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Side Panel for Create/Edit/View */}
      <SidePanel
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        title={panelMode === 'create' ? 'New Purchase Order' : panelMode === 'edit' ? 'Edit Purchase Order' : 'Purchase Order Details'}
        subtitle={panelMode === 'view' && selectedOrder ? `${selectedOrder.po_number || 'PO-' + selectedOrder.id.slice(0, 8)}` : ''}
        minimized={panelMinimized}
        onMinimize={() => setPanelMinimized(!panelMinimized)}
      >
        {panelMode === 'view' && selectedOrder ? (
          // View Mode
          <>
            <SidePanelSection title="Order Information">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-stone-500 uppercase">PO Number</p>
                  <p className="font-medium">{selectedOrder.po_number || `PO-${selectedOrder.id.slice(0, 8)}`}</p>
                </div>
                <div>
                  <p className="text-xs text-stone-500 uppercase">Status</p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CONFIG[selectedOrder.status]?.color}`}>
                    {STATUS_CONFIG[selectedOrder.status]?.label}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-stone-500 uppercase">Project</p>
                  <p className="font-medium">{getProjectName(selectedOrder.project_id)}</p>
                </div>
                <div>
                  <p className="text-xs text-stone-500 uppercase">Vendor</p>
                  <p className="font-medium">{getVendorName(selectedOrder.vendor_id)}</p>
                </div>
              </div>
            </SidePanelSection>

            <SidePanelDivider />

            <SidePanelSection title="Items">
              {(selectedOrder.items || []).map((item, idx) => (
                <div key={idx} className="p-3 bg-stone-50 rounded-lg mb-2">
                  <div className="flex justify-between">
                    <span className="font-medium">{item.item_name}</span>
                    <span className="font-bold">₹{item.amount?.toLocaleString()}</span>
                  </div>
                  <div className="text-sm text-stone-500">
                    {item.quantity} {item.unit} × ₹{item.rate?.toLocaleString()}
                  </div>
                </div>
              ))}
              <div className="flex justify-between pt-3 border-t mt-3">
                <span className="font-semibold">Total</span>
                <span className="font-bold text-lg">
                  ₹{(selectedOrder.items || []).reduce((sum, i) => sum + (i.amount || 0), 0).toLocaleString()}
                </span>
              </div>
            </SidePanelSection>

            <SidePanelActions>
              <Button variant="outline" className="flex-1" onClick={() => openEditPanel(selectedOrder)}>
                <Edit2 size={16} className="mr-2" /> Edit
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => handleExportPDF(selectedOrder)}>
                <FileText size={16} className="mr-2" /> PDF
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => handleExportExcel(selectedOrder)}>
                <Download size={16} className="mr-2" /> Excel
              </Button>
            </SidePanelActions>
          </>
        ) : (
          // Create/Edit Mode
          <>
            <SidePanelSection title="Basic Information">
              <div className="space-y-4">
                <div>
                  <Label>Project *</Label>
                  <Select value={formData.project_id} onValueChange={(value) => setFormData({ ...formData, project_id: value })}>
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Vendor *</Label>
                  <Select value={formData.vendor_id} onValueChange={(value) => setFormData({ ...formData, vendor_id: value })}>
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Select vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      {vendors.map(v => (
                        <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Delivery Date</Label>
                  <Input
                    type="date"
                    value={formData.delivery_date}
                    onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })}
                    className="bg-white"
                  />
                </div>
                <div>
                  <Label>Delivery Address</Label>
                  <Textarea
                    value={formData.delivery_address}
                    onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                    placeholder="Enter delivery address"
                    className="bg-white"
                    rows={2}
                  />
                </div>
              </div>
            </SidePanelSection>

            <SidePanelDivider />

            <SidePanelSection title="Items">
              <div className="space-y-3">
                {formData.items.map((item, idx) => (
                  <div key={idx} className="p-3 bg-stone-50 rounded-lg space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-stone-600">Item {idx + 1}</span>
                      {formData.items.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => handleRemoveItem(idx)}>
                          <Trash2 size={14} className="text-red-500" />
                        </Button>
                      )}
                    </div>
                    <Input
                      placeholder="Item name *"
                      value={item.item_name}
                      onChange={(e) => handleItemChange(idx, 'item_name', e.target.value)}
                      className="bg-white"
                    />
                    <Input
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                      className="bg-white"
                    />
                    <div className="grid grid-cols-4 gap-2">
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(idx, 'quantity', Number(e.target.value))}
                        className="bg-white"
                      />
                      <Select value={item.unit} onValueChange={(value) => handleItemChange(idx, 'unit', value)}>
                        <SelectTrigger className="bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {UNIT_OPTIONS.map(u => (
                            <SelectItem key={u} value={u}>{u}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        placeholder="Rate"
                        value={item.rate}
                        onChange={(e) => handleItemChange(idx, 'rate', Number(e.target.value))}
                        className="bg-white"
                      />
                      <div className="flex items-center justify-end font-medium">
                        ₹{item.amount?.toLocaleString() || 0}
                      </div>
                    </div>
                  </div>
                ))}
                <Button variant="outline" onClick={handleAddItem} className="w-full">
                  <Plus size={16} className="mr-2" /> Add Item
                </Button>
                <div className="flex justify-between pt-3 border-t">
                  <span className="font-semibold">Total</span>
                  <span className="font-bold text-lg">₹{calculateTotal().toLocaleString()}</span>
                </div>
              </div>
            </SidePanelSection>

            <SidePanelDivider />

            <SidePanelSection title="Terms & Conditions">
              <Textarea
                value={formData.terms}
                onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
                placeholder="Enter terms and conditions"
                className="bg-white"
                rows={4}
              />
            </SidePanelSection>

            <SidePanelActions>
              <Button variant="outline" onClick={() => setPanelOpen(false)}>
                Cancel
              </Button>
              {panelMode === 'edit' && (
                <Button 
                  variant="outline" 
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  <Trash2 size={16} className="mr-2" /> Delete
                </Button>
              )}
              <Button 
                onClick={panelMode === 'create' ? handleCreate : handleUpdate}
                className="apple-button"
              >
                {panelMode === 'create' ? 'Create Order' : 'Save Changes'}
              </Button>
            </SidePanelActions>
          </>
        )}
      </SidePanel>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Purchase Order?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the purchase order.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
